/**
 * Buddy Group Admin API service — frontend HTTP calls.
 *
 * Talks to the /api/buddy-group-admin-ops/* admin endpoints.
 * Uses the shared httpClient with cookie-based auth.
 */

import { httpClient } from '@/api/http-client';
import type {
  BuddyGroupSummary,
  BuddyGroupDetail,
  UpdateBuddyGroupPayload,
  AddMemberPayload,
  BuddyGroupMemberView,
} from '../types/buddy-group';
import type { BuddyGroupConflict, ResolveConflictPayload } from '../types/conflict';

// ─── Query Key Factory ──────────────────────────────────────────────────────

export const buddyGroupAdminKeys = {
  all: ['buddy-group-admin'] as const,
  lists: () => [...buddyGroupAdminKeys.all, 'list'] as const,
  list: (rodeoId: string) => [...buddyGroupAdminKeys.lists(), rodeoId] as const,
  details: () => [...buddyGroupAdminKeys.all, 'detail'] as const,
  detail: (id: string) => [...buddyGroupAdminKeys.details(), id] as const,
  conflicts: (rodeoId: string) => [...buddyGroupAdminKeys.all, 'conflicts', rodeoId] as const,
};

// ─── Response wrappers ──────────────────────────────────────────────────────
// Backend returns { data: T } for reads and { data: T, message?: string } for writes.

interface ApiListResponse<T> {
  data: T[];
  total: number;
}

interface ApiSingleResponse<T> {
  data: T;
}

interface ApiDetailResponse {
  data: BuddyGroupDetail;
  conflicts: BuddyGroupConflict[];
}

interface ApiConflictListResponse {
  data: BuddyGroupConflict[];
  total: number;
  pending_count: number;
}

// ─── Service functions ──────────────────────────────────────────────────────

export async function fetchBuddyGroups(rodeoId: string): Promise<{
  groups: BuddyGroupSummary[];
  total: number;
}> {
  const res = await httpClient.get<ApiListResponse<BuddyGroupSummary>>(
    `/buddy-group-admin-ops/rodeo/${rodeoId}/groups`
  );
  return { groups: res.data, total: res.total };
}

export async function fetchBuddyGroup(id: string): Promise<{
  group: BuddyGroupDetail;
  conflicts: BuddyGroupConflict[];
}> {
  const res = await httpClient.get<ApiDetailResponse>(
    `/buddy-group-admin-ops/${id}`
  );
  return { group: res.data, conflicts: res.conflicts };
}

export async function updateBuddyGroup(
  id: string,
  payload: UpdateBuddyGroupPayload
): Promise<{ group: BuddyGroupDetail; conflicts: BuddyGroupConflict[] }> {
  const res = await httpClient.request<ApiDetailResponse>(
    `/buddy-group-admin-ops/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
  return { group: res.data, conflicts: res.conflicts };
}

export async function addBuddyGroupMember(
  groupId: string,
  payload: AddMemberPayload
): Promise<BuddyGroupMemberView> {
  const res = await httpClient.post<ApiSingleResponse<BuddyGroupMemberView>>(
    `/buddy-group-admin-ops/${groupId}/members`,
    payload
  );
  return res.data;
}

export async function removeBuddyGroupMember(
  groupId: string,
  memberProfileId: string
): Promise<void> {
  await httpClient.delete(
    `/buddy-group-admin-ops/${groupId}/members/${memberProfileId}`
  );
}

export async function dissolveBuddyGroup(id: string): Promise<void> {
  await httpClient.delete(`/buddy-group-admin-ops/${id}/dissolve`);
}

export async function fetchRodeoConflicts(rodeoId: string): Promise<{
  conflicts: BuddyGroupConflict[];
  total: number;
  pending_count: number;
}> {
  const res = await httpClient.get<ApiConflictListResponse>(
    `/buddy-group-admin-ops/rodeo/${rodeoId}/conflicts`
  );
  return {
    conflicts: res.data,
    total: res.total,
    pending_count: res.pending_count,
  };
}

export async function resolveConflict(
  conflictId: string,
  payload: ResolveConflictPayload
): Promise<BuddyGroupConflict> {
  const res = await httpClient.post<ApiSingleResponse<BuddyGroupConflict>>(
    `/buddy-group-admin-ops/conflicts/${conflictId}/resolve`,
    payload
  );
  return res.data;
}

// ─── User profile search for member selector ──────────────────────────────

export interface UserProfileSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
}

export async function searchUserProfiles(
  query: string
): Promise<UserProfileSearchResult[]> {
  const res = await httpClient.get<{ data: UserProfileSearchResult[] }>(
    `/buddy-group-admin-ops/search-users?q=${encodeURIComponent(query)}&limit=20`
  );
  return res.data;
}
